import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  IMessage,
  IStatus,
  IErrorPayload,
  IJSONAPIDataAttributes,
} from "shared/types";
import { addTemporaryToast } from "shared/components/Toast";
import { setMessageModal } from "shared/components/MessageModal";
import { copy } from "shared/assets";
import { IStoreState } from "App/types";
import {
  IRegisterCredentials,
  IUserCredentials,
  IVerifyCredentials,
  IUser,
  IChangePassword,
  IResetPassword,
  IUserPatchData,
  AUTH_ACTIONS,
} from "Auth/types";
import { AuthService, InternalAuthService } from "Auth/services";
import { merge, omit } from "lodash-es";

export const login = createAsyncThunk<
  IUser,
  IUserCredentials,
  { rejectValue: IErrorPayload }
>(
  AUTH_ACTIONS.login,
  async (userCredentials, { rejectWithValue, dispatch }) => {
    try {
      const user = await AuthService.login(userCredentials);
      dispatch(
        addTemporaryToast({
          message: copy.login.success,
          status: IStatus.success,
        })
      );
      return user;
    } catch (error) {
      return rejectWithValue(error as IErrorPayload);
    }
  },
  {
    // Check if the user is already authenticated, if so the action is not dispatched
    condition: (_, { getState }) => {
      const { currentUser } = (getState() as IStoreState).auth;
      if (currentUser) {
        return false;
      }
    },
  }
);

export const getCurrentUser = createAsyncThunk<
  IUser,
  {
    force?: boolean;
    userAttributes?: IJSONAPIDataAttributes;
  } | void,
  { rejectValue: IErrorPayload }
>(AUTH_ACTIONS.getCurrentUser, async (data, { rejectWithValue, dispatch }) => {
  const { force, userAttributes } = data || {};

  try {
    const newUser = await AuthService.getCurrentUser(force || false);

    // if this is the first user call
    // get internal user attributes from internal auth service
    if (!userAttributes) {
      dispatch(getInternalUser({ newUser }));
    }

    return merge({}, newUser, { attributes: userAttributes });
  } catch (error) {
    return rejectWithValue(error as IErrorPayload);
  }
});

export const register = createAsyncThunk<
  IMessage,
  IRegisterCredentials,
  { rejectValue: IErrorPayload }
>(
  AUTH_ACTIONS.register,
  async (userCredentials, { rejectWithValue, dispatch }) => {
    try {
      const message = await AuthService.register(userCredentials);
      dispatch(setMessageModal({ message }));
      return message;
    } catch (error) {
      return rejectWithValue(error as IErrorPayload);
    }
  }
);

export const logout = createAsyncThunk<
  void,
  boolean | void,
  { rejectValue: IErrorPayload }
>(AUTH_ACTIONS.logout, async (global, { rejectWithValue }) => {
  try {
    return await AuthService.logout(global || false);
  } catch (error) {
    return rejectWithValue(error as IErrorPayload);
  }
});

export const verifyAccount = createAsyncThunk<
  IMessage,
  IVerifyCredentials,
  { rejectValue: IErrorPayload }
>(
  AUTH_ACTIONS.verifyAccount,
  async (verifyCredentials, { rejectWithValue, dispatch }) => {
    try {
      const message = await AuthService.verifyAccount(verifyCredentials);
      dispatch(setMessageModal({ message }));
      return message;
    } catch (error: any) {
      dispatch(setMessageModal({ message: error.message }));
      return rejectWithValue(error as IErrorPayload);
    }
  }
);

export const requestChangeEmail = createAsyncThunk<
  void,
  IUserPatchData,
  { rejectValue: IErrorPayload }
>(
  AUTH_ACTIONS.requestChangeEmail,
  async (changeEmailData, { rejectWithValue, dispatch }) => {
    try {
      const message = await InternalAuthService.requestChangeEmail(
        changeEmailData
      );
      dispatch(setMessageModal({ message }));
    } catch (error) {
      return rejectWithValue(error as IErrorPayload);
    }
  }
);

export const confirmEmail = createAsyncThunk<
  void,
  string,
  { rejectValue: IErrorPayload }
>(AUTH_ACTIONS.confirmEmail, async (token, { rejectWithValue, dispatch }) => {
  try {
    const message = await InternalAuthService.confirmEmail(token);

    dispatch(setMessageModal({ message }));

    // Update user in state (to show new email)
    dispatch(getCurrentUser({ force: true }));
  } catch (error: any) {
    dispatch(setMessageModal({ message: error.message }));
    return rejectWithValue(error as IErrorPayload);
  }
});

export const changePassword = createAsyncThunk<
  void,
  IChangePassword,
  { rejectValue: IErrorPayload }
>(
  AUTH_ACTIONS.changePassword,
  async (passwordCredentials, { rejectWithValue, dispatch }) => {
    try {
      const message = await AuthService.changePassword(passwordCredentials);
      dispatch(setMessageModal({ message }));
    } catch (error: any) {
      return rejectWithValue(error as IErrorPayload);
    }
  }
);

export const requestResetPassword = createAsyncThunk<
  void,
  string,
  { rejectValue: IErrorPayload }
>(
  AUTH_ACTIONS.requestResetPassword,
  async (email, { rejectWithValue, dispatch }) => {
    try {
      const message = await AuthService.requestResetPassword(email);
      dispatch(setMessageModal({ message }));
    } catch (error) {
      return rejectWithValue(error as IErrorPayload);
    }
  }
);

export const resetPassword = createAsyncThunk<
  void,
  IResetPassword,
  { rejectValue: IErrorPayload }
>(
  AUTH_ACTIONS.resetPassword,
  async (resetPasswordCredentials, { rejectWithValue, dispatch }) => {
    try {
      const message = await AuthService.resetPassword(resetPasswordCredentials);
      dispatch(setMessageModal({ message }));
    } catch (error) {
      return rejectWithValue(error as IErrorPayload);
    }
  }
);

export const getInternalUser = createAsyncThunk<
  IUser | null,
  {
    newUser?: IUser;
  } | void,
  { rejectValue: IErrorPayload }
>(
  AUTH_ACTIONS.getCurrentInternalUser,
  async (data, { rejectWithValue, getState }) => {
    try {
      const { newUser } = data || {};
      const stateUser = (getState() as IStoreState).auth.currentUser;
      const currentUser = newUser || stateUser;
      const { id: userId } = currentUser || {};

      if (userId) {
        const editedUser = await InternalAuthService.getUser(userId);
        const userAttributes = Object.values(editedUser?.user)[0]?.attributes;

        return merge({}, omit(currentUser, "attributes") as IUser, {
          attributes: userAttributes,
        });
      }

      return null;
    } catch (error) {
      return rejectWithValue(error as IErrorPayload);
    }
  }
);

export const editCurrentUser = createAsyncThunk<
  IUser | null,
  IUserPatchData,
  { rejectValue: IErrorPayload }
>(
  AUTH_ACTIONS.editCurrentUser,
  async (editUserData, { rejectWithValue, dispatch, getState }) => {
    try {
      const currentUser = (getState() as IStoreState).auth.currentUser;
      const { id: userId } = currentUser || {};

      if (userId) {
        const editedUser = await InternalAuthService.editUser(
          userId,
          editUserData
        );

        const userAttributes = Object.values(editedUser?.user)[0]?.attributes;

        // Update user in state with internal user attributes
        // and force refresh user in cognito
        dispatch(
          getCurrentUser({
            force: true,
            userAttributes,
          })
        );

        dispatch(
          addTemporaryToast({
            message: copy.toast.changed,
            status: IStatus.success,
          })
        );

        return merge({}, currentUser, { attributes: userAttributes });
      }

      return null;
    } catch (error) {
      return rejectWithValue(error as IErrorPayload);
    }
  }
);

export const deleteAccount = createAsyncThunk<
  void,
  void,
  { rejectValue: IErrorPayload }
>(AUTH_ACTIONS.deleteAccount, async (_, { rejectWithValue, getState }) => {
  try {
    const userId = (getState() as IStoreState).auth.currentUser?.id;

    if (userId) {
      await InternalAuthService.deleteAccount(userId);
    }
  } catch (error: any) {
    return rejectWithValue(error as IErrorPayload);
  }
});
